import React, { useCallback, useEffect, useMemo, useState } from "react";
import type {
  MonthlyReportId,
  OperatorAuditPoint,
  OperatorChangeSetRow,
  OperatorSiteDetailV1,
  SeoAuditRunId,
  SiteId,
} from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import type { OperatorCopy } from "../copy";
import { formatDay, formatMonth, operatorFetch, relativeTime } from "../format";
import { SiteMark, siteInitials, siteTone } from "../SiteMark";
import { CHIP, setupFacts, siteStage } from "./data";
import type { MC2Copy } from "./copy";
import type { DecisionAction, RecommendationAction } from "./PipelinePage";

type SiteTab = "week" | "changes" | "audit" | "search" | "setup" | "client" | "reports";

export function SitePage({
  siteId,
  copy,
  ops,
  locale,
  now,
  version,
  busyKey,
  onBack,
  onDecide,
  onRecommendation,
  onRunAudit,
  onOpenReport,
  onOpenAudit,
}: {
  siteId: SiteId;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  version: number;
  busyKey: string;
  onBack(): void;
  onDecide(changeSetId: string, action: DecisionAction, busyKey: string): void;
  onRecommendation(recommendationId: string, action: RecommendationAction, busyKey: string): void;
  onRunAudit(siteId: SiteId, busyKey: string): void;
  onOpenReport(reportId: MonthlyReportId): void;
  onOpenAudit(auditId: SeoAuditRunId): void;
}) {
  const t = copy.site;
  const [detail, setDetail] = useState<OperatorSiteDetailV1 | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<SiteTab>("week");
  const [auditReport, setAuditReport] = useState<import("@siteagain/shared-types").SeoAuditReportV1 | null>(null);
  const [auditReportLoaded, setAuditReportLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await operatorFetch<{ detail: OperatorSiteDetailV1 }>(`/operator/sites/${siteId}`);
      setDetail(response.detail);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : ops.drawer.loadFailed);
    }
  }, [siteId, ops.drawer.loadFailed]);

  useEffect(() => { setDetail(null); setTab("week"); }, [siteId]);
  useEffect(() => { void load(); }, [load, version]);
  const auditRunning = detail?.audits[0]?.status === "queued" || detail?.audits[0]?.status === "running";
  useEffect(() => {
    if (!auditRunning) return;
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, 8_000);
    return () => window.clearInterval(timer);
  }, [auditRunning, load]);

  const site = detail?.site;
  const facts = site ? setupFacts(site) : [];
  const latestAudit = detail?.audits[0] ?? null;
  useEffect(() => {
    if (tab !== "search" || !latestAudit) return;
    setAuditReportLoaded(false);
    setAuditReport(null);
    let active = true;
    void operatorFetch<{ detail: { run: { report: import("@siteagain/shared-types").SeoAuditReportV1 | null } } }>(`/operator/audits/${latestAudit.id}`)
      .then((response) => { if (active) { setAuditReport(response.detail.run.report); setAuditReportLoaded(true); } })
      .catch(() => { if (active) { setAuditReport(null); setAuditReportLoaded(true); } });
    return () => { active = false; };
  }, [tab, latestAudit?.id, version]);
  const latestDeployed = detail?.changeSets
    .filter((row) => row.status === "deployed" && row.deployedAt)
    .sort((left, right) => (right.deployedAt ?? "").localeCompare(left.deployedAt ?? ""))[0] ?? null;
  const shipped = useMemo(() => detail?.changeSets.filter((row) => row.deployedAt && now - new Date(row.deployedAt).getTime() < 7 * 86_400_000) ?? [], [detail?.changeSets, now]);
  const pendingRecs = detail?.recommendations.filter((row) => ["new", "viewed", "saved", "previewing", "previewed"].includes(row.status)) ?? [];
  const days = useMemo(() => buildDays(detail, now, locale, copy, ops, {
    onOpenChange: () => setTab("changes"),
    onOpenAudit,
    onOpenReport,
  }), [detail, now, locale, copy, ops, onOpenAudit, onOpenReport]);
  const pipe = useMemo(() => buildPipe(detail, ops, locale), [detail, ops, locale]);

  const tabs: Array<{ id: SiteTab; label: string }> = [
    { id: "week", label: t.week },
    { id: "changes", label: `${t.changes} ${detail?.changeSets.length ?? 0}` },
    { id: "audit", label: t.audit },
    { id: "search", label: t.search },
    { id: "setup", label: t.setup },
    { id: "client", label: t.client },
    { id: "reports", label: t.reports },
  ];

  return (
    <div className="mc2-site">
      <div className="mc2-breadcrumb">
        <button type="button" onClick={onBack}>{t.breadcrumb}</button>
        <span>/</span>
        <span>{site?.name ?? "…"}</span>
      </div>

      {error && <div className="mc2-banner bad" role="alert">{error}</div>}
      {!detail && !error && <div className="mc2-loading">{ops.status.loading}</div>}

      {detail && site && (
        <>
          <div className="mc2-sitehead">
            <div className="identity">
              <span className="bigmark" style={{ background: siteTone(site.id) }}>{siteInitials(site.name)}</span>
              <div>
                <h1>{site.name}</h1>
                <div className="chips">
                  <span className={CHIP.ink}>{siteStage(site, locale).label}</span>
                  <span className={CHIP[site.health === "critical" ? "down" : site.health === "healthy" ? "up" : "warn"]}>{ops.health[site.health] ?? site.health}</span>
                  <span className={CHIP.neutral}>{site.productionDomain ?? site.slug}</span>
                </div>
              </div>
            </div>
            <div className="mc2-topactions">
              <div className="mc2-weekpick">
                <span className="mc2-weeklbl">{t.last7}</span>
              </div>
              {site.productionDomain && (
                <a className="mc2-btn ghost" href={`https://${site.productionDomain.replace(/^https?:\/\//u, "")}`} target="_blank" rel="noreferrer">{locale === "fr" ? "Ouvrir le site" : "Open live site"} ↗</a>
              )}
              <button type="button" className="mc2-btn" disabled={busyKey === `site:${siteId}:audit` || auditRunning} onClick={() => onRunAudit(siteId, `site:${siteId}:audit`)}>
                {busyKey === `site:${siteId}:audit` || auditRunning ? ops.action.working : ops.action.runAudit}
              </button>
            </div>
          </div>

          <div className="mc2-tabs" role="tablist" aria-label={site.name}>
            {tabs.map((entry) => (
              <button key={entry.id} type="button" role="tab" aria-selected={tab === entry.id} className={`mc2-sitetabs-tab mc2-tab${tab === entry.id ? " on" : ""}`} onClick={() => setTab(entry.id)}>
                {entry.label}
              </button>
            ))}
          </div>

          {tab === "week" && (
            <div className="mc2-sitecols">
              <div className="mc2-col-main">
                <section className="mc2-card" style={{ padding: "24px 26px" }} aria-label={t.summaryEyebrow}>
                  <div className="mc2-eyebrow">{t.summaryEyebrow}</div>
                  <p className="mc2-serif" style={{ fontSize: 28, lineHeight: 1.25, margin: "10px 0 12px" }}>
                    {t.summary(shipped.length, site.awaitingApproval, site.latestSeoScore, site.openIncidents)}
                  </p>
                  <div className="mc2-muted" style={{ fontSize: 13 }}>{locale === "fr" ? "Dérivé des enregistrements de ce site. Aucun chiffre n'est inventé." : "Derived from this site's stored records. No figure is invented."}</div>
                </section>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 12 }}>
                  <ScoreCard label={t.scores.score} value={site.latestSeoScore === null ? "—" : String(Math.round(site.latestSeoScore))} note={site.latestSeoScoreAt ? relativeTime(site.latestSeoScoreAt, locale, now) : ops.fleet.scoreNone} />
                  <ScoreCard label={t.scores.shipped} value={String(shipped.length)} note={t.last7} />
                  <ScoreCard label={t.scores.waiting} value={String(site.awaitingApproval)} note={t.waitingNote} />
                  <ScoreCard label={t.scores.recs} value={String(pendingRecs.length)} note={t.toDecide} />
                  <ScoreCard label={t.scores.enquiries} value={String(detail.enquiries.last28Days)} note={t.enquiriesValue} />
                </div>

                <section className="mc2-card" aria-labelledby="mc2-day">
                  <div className="mc2-cardhead">
                    <h2 id="mc2-day">{t.dayByDay}</h2>
                    <span className="mc2-muted" style={{ fontSize: 13 }}>{t.dayHint}</span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <div className="mc2-daygrid">
                      {days.map((day) => (
                        <div className="mc2-day" key={day.iso}>
                          <div className="mc2-eyebrow">{day.label}</div>
                          {day.events.length === 0 ? <div className="mc2-muted" style={{ fontSize: 12 }}>—</div> : day.events.map((event, index) => (
                            <button key={`${day.iso}:${index}`} type="button" className={`mc2-ev ${event.kind}`} style={{ border: 0, textAlign: "left", cursor: event.onClick ? "pointer" : "default", fontFamily: "inherit" }} onClick={event.onClick}>
                              {event.text}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="mc2-card" aria-labelledby="mc2-pipe">
                  <div className="mc2-cardhead">
                    <h2 id="mc2-pipe">{t.changePipeline}</h2>
                    <span className="mc2-muted" style={{ fontSize: 13 }}>{t.changeHint}</span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <div className="mc2-pipe">
                      {pipe.map((column) => (
                        <div className="mc2-pipecol" key={column.label}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 4px" }}>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{column.label}</span>
                            <span className="mc2-mono mc2-muted" style={{ fontSize: 12 }}>{column.cards.length}</span>
                          </div>
                          {column.cards.length === 0 ? <div className="mc2-muted" style={{ fontSize: 12, padding: "4px" }}>—</div> : column.cards.map((card) => (
                            <div className="mc2-kc" key={card.key}>
                              <div style={{ fontWeight: 500 }}>{card.title}</div>
                              <div className="mc2-muted" style={{ fontSize: 12, marginTop: 4 }}>{card.meta}</div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </div>

              <div className="mc2-col-side">
                <AuditCard latest={latestAudit} copy={copy} ops={ops} locale={locale} now={now} busy={busyKey === `site:${siteId}:audit`} auditRunning={auditRunning} onOpen={onOpenAudit} onRun={() => onRunAudit(siteId, `site:${siteId}:audit`)} />
                <section className="mc2-card" aria-labelledby="mc2-site-setup">
                  <div className="mc2-cardhead">
                    <h2 id="mc2-site-setup">{t.setupTitle}</h2>
                    <span className={CHIP[facts.every((fact) => fact.ok) ? "up" : "warn"]}>{facts.every((fact) => fact.ok) ? t.setupComplete : t.setupPartial}</span>
                  </div>
                  <div style={{ padding: "6px 20px 12px" }}>
                    {facts.map((fact) => (
                      <div className="mc2-chk" key={fact.key}>
                        <span className={fact.ok ? "mc2-ok" : "mc2-sbox no"} style={fact.ok ? undefined : { width: 20, height: 20 }}>{fact.ok ? "✓" : ""}</span>
                        {copy.fleet.setup[fact.key]}
                        <span className="mc2-muted" style={{ marginLeft: "auto", fontSize: 12 }}>{fact.ok ? copy.fleet.setupOk : copy.fleet.setupMissing}</span>
                      </div>
                    ))}
                    <div className="mc2-chk">
                      <span className={detail.searchConsole.connection ? "mc2-ok" : "mc2-sbox no"} style={detail.searchConsole.connection ? undefined : { width: 20, height: 20 }}>{detail.searchConsole.connection ? "✓" : ""}</span>
                      {t.searchTitle}
                      <span className="mc2-muted" style={{ marginLeft: "auto", fontSize: 12 }}>{detail.searchConsole.connection ? ops.drawer.searchStatus[detail.searchConsole.connection.status] ?? detail.searchConsole.connection.status : t.notConnected}</span>
                    </div>
                  </div>
                </section>
                <section className="mc2-card" aria-labelledby="mc2-site-client">
                  <div className="mc2-cardhead"><h2 id="mc2-site-client">{t.clientTitle}</h2></div>
                  <div style={{ padding: "12px 20px 16px", fontSize: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.weeklyEmail}</span><span className="mc2-muted">{t.weeklyEmailValue}</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.approved}</span><span className="mc2-muted">{latestDeployed ? latestDeployed.summary : "—"}</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.scores.enquiries}</span><span className="mc2-mono">{detail.enquiries.last28Days}</span></div>
                    <button type="button" className="mc2-btn ghost sm" style={{ alignSelf: "flex-start", marginTop: 4 }} onClick={() => setTab("client")}>{t.openClient}</button>
                  </div>
                </section>
                <section className="mc2-card" aria-labelledby="mc2-guardrails">
                  <div className="mc2-cardhead"><h2 id="mc2-guardrails">{t.guardrails}</h2></div>
                  <p className="mc2-muted" style={{ margin: 0, padding: "12px 20px 16px", fontSize: 13 }}>{t.guardrailsBody}</p>
                </section>
              </div>
            </div>
          )}

          {tab === "changes" && (
            <div className="mc2-sitecols">
              <section className="mc2-card mc2-col-main" aria-labelledby="mc2-changes">
                <div className="mc2-cardhead"><h2 id="mc2-changes">{ops.drawer.changes}</h2><span className="mc2-muted" style={{ fontSize: 13 }}>{detail.changeSets.length}</span></div>
                {detail.changeSets.length === 0 ? <p className="mc2-empty">{t.noChanges}</p> : detail.changeSets.map((row) => (
                  <ChangeRow key={row.id} row={row} copy={copy} ops={ops} locale={locale} now={now} busyKey={busyKey} canRollback={row.id === latestDeployed?.id} onDecide={onDecide} onRecommendation={onRecommendation} />
                ))}
              </section>
              <section className="mc2-card mc2-col-side" aria-labelledby="mc2-recs">
                <div className="mc2-cardhead"><h2 id="mc2-recs">{ops.drawer.recommendations}</h2><span className="mc2-muted" style={{ fontSize: 13 }}>{detail.recommendations.length}</span></div>
                {detail.recommendations.length === 0 ? <p className="mc2-empty">{t.noRecs}</p> : detail.recommendations.map((row) => (
                  <div className="mc2-sec" key={row.id} style={{ borderBottom: "1px solid var(--line-soft)" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                      <span className={CHIP.neutral}>{ops.drawer.recommendationStatus[row.status]}</span>
                      <span className={CHIP.neutral}>{ops.drawer.type[row.type]}</span>
                      <span className={CHIP.neutral}>{ops.pipeline.safety[row.safetyClass]}</span>
                    </div>
                    <div style={{ fontWeight: 500 }}>{row.title}</div>
                    {["new", "viewed", "saved"].includes(row.status) && (
                      <div className="mc2-actions" style={{ marginTop: 8 }}>
                        <button type="button" className="mc2-btn xs" disabled={busyKey.startsWith(`recommendation:${row.id}:`)} onClick={() => onRecommendation(row.id, "preview", `recommendation:${row.id}:preview`)}>{ops.action.buildPreview}</button>
                        <button type="button" className="mc2-btn ghost xs" disabled={busyKey.startsWith(`recommendation:${row.id}:`)} onClick={() => onRecommendation(row.id, "skip", `recommendation:${row.id}:skip`)}>{ops.action.dismiss}</button>
                      </div>
                    )}
                  </div>
                ))}
              </section>
            </div>
          )}

          {tab === "audit" && (
            <div className="mc2-sitecols">
              <div className="mc2-col-main">
                <AuditCard latest={latestAudit} copy={copy} ops={ops} locale={locale} now={now} busy={busyKey === `site:${siteId}:audit`} auditRunning={auditRunning} onOpen={onOpenAudit} onRun={() => onRunAudit(siteId, `site:${siteId}:audit`)} />
              </div>
              <section className="mc2-card mc2-col-side" aria-labelledby="mc2-audit-history">
                <div className="mc2-cardhead"><h2 id="mc2-audit-history">{ops.audit.history}</h2></div>
                {detail.audits.length <= 1 ? <p className="mc2-empty">{ops.drawer.noAudits}</p> : detail.audits.slice(1).map((audit) => (
                  <div className="mc2-li" key={audit.id}>
                    <strong style={{ fontFamily: "var(--font-serif)", fontSize: 22 }}>{audit.score !== null ? Math.round(audit.score) : "—"}</strong>
                    <span>{ops.activity.status[audit.status] ?? audit.status}</span>
                    <span className="mc2-muted" style={{ marginLeft: "auto" }}>{relativeTime(audit.capturedAt, locale, now)}</span>
                    {(audit.status === "completed" || audit.status === "partial") && (
                      <button type="button" className="mc2-btn ghost xs" onClick={() => onOpenAudit(audit.id)}>{ops.audit.open}</button>
                    )}
                  </div>
                ))}
              </section>
            </div>
          )}

          {tab === "search" && (
            <div className="mc2-sitecols">
              <div className="mc2-col-main" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <section className="mc2-card" aria-labelledby="mc2-search">
                  <div className="mc2-cardhead"><h2 id="mc2-search">{t.searchTitle}</h2></div>
                  {!detail.searchConsole.connection ? (
                    <p className="mc2-empty">{ops.drawer.noSearch}</p>
                  ) : (
                    <div className="mc2-sec">
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <span className={CHIP[detail.searchConsole.connection.status === "active" ? "up" : detail.searchConsole.connection.status === "needs_reconnect" ? "down" : "warn"]}>
                          {ops.drawer.searchStatus[detail.searchConsole.connection.status] ?? detail.searchConsole.connection.status}
                        </span>
                        {detail.searchConsole.connection.dataThrough && <span className={CHIP.neutral}>{ops.drawer.searchThrough(formatDay(detail.searchConsole.connection.dataThrough, locale))}</span>}
                      </div>
                      <dl className="mc-facts">
                        <div><dt>{ops.drawer.searchProperty}</dt><dd>{detail.searchConsole.connection.property ?? "—"}</dd></div>
                        <div><dt>{ops.drawer.searchAccount}</dt><dd>{detail.searchConsole.connection.googleEmail ?? "—"}</dd></div>
                      </dl>
                      {detail.searchConsole.connection.lastSyncedAt && <p className="mc2-muted" style={{ fontSize: 13 }}>{ops.drawer.searchLastPull(relativeTime(detail.searchConsole.connection.lastSyncedAt, locale, now))}</p>}
                      <h3 style={{ fontSize: 14, margin: "16px 0 8px" }}>{ops.drawer.searchRuns}</h3>
                      {detail.searchConsole.recentRuns.length === 0 ? <p className="mc2-muted">{ops.drawer.searchNoRuns}</p> : detail.searchConsole.recentRuns.map((run) => (
                        <div className="mc2-li" key={run.id}>
                          <span>{ops.drawer.runKind[run.kind] ?? run.kind}</span>
                          <span className="mc2-muted mc2-mono">{run.fromDay} → {run.toDay}</span>
                          <span className={CHIP[run.status === "failed" ? "down" : run.status === "done" ? "up" : "warn"]}>{ops.drawer.runStatus[run.status] ?? run.status}</span>
                          <span className="mc2-muted">{run.status === "done" ? ops.drawer.runRows(run.rowsWritten) : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="mc2-card" aria-labelledby="mc2-ai">
                  <div className="mc2-cardhead">
                    <h2 id="mc2-ai">{ops.audit.visibility}</h2>
                    {latestAudit && <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 13 }} onClick={() => onOpenAudit(latestAudit.id)}>{ops.audit.openReport} →</button>}
                  </div>
                  {!auditReportLoaded ? (
                    <p className="mc2-empty">{latestAudit ? ops.status.loading : ops.audit.notAssessedBody}</p>
                  ) : !auditReport ? (
                    <p className="mc2-empty">{latestAudit?.status === "failed" ? ops.audit.failedBody : ops.audit.notAssessedBody}</p>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 0, borderBottom: "1px solid var(--line-soft)" }}>
                        <AiStat label={ops.audit.aiCitation} value={auditReport.tregInsights?.aiCitationSharePercent != null ? `${Math.round(auditReport.tregInsights.aiCitationSharePercent)}%` : "—"} />
                        <AiStat label={ops.audit.mentions} value={auditReport.tregInsights?.llmMentions ? String(auditReport.tregInsights.llmMentions.totalMentions) : "—"} />
                        <AiStat label={ops.audit.referringDomains} value={auditReport.tregInsights?.backlinkSummary?.referringDomains != null ? String(auditReport.tregInsights.backlinkSummary.referringDomains) : "—"} />
                        <AiStat label={ops.audit.authority} value={auditReport.tregInsights?.backlinkSummary?.domainAuthority != null ? String(auditReport.tregInsights.backlinkSummary.domainAuthority) : "—"} />
                        <AiStat label={ops.audit.rankedKeywords} value={auditReport.tregInsights?.domainKeywords ? String(auditReport.tregInsights.domainKeywords.top10Keywords) : "—"} />
                      </div>
                      <div className="mc2-sec">
                        <div className="mc2-eyebrow" style={{ marginBottom: 8 }}>{ops.audit.keywords}</div>
                        {auditReport.metrics.queryMetrics.length === 0 ? <p className="mc2-muted">{ops.audit.keywordsHint}</p> : (
                          <div className="mc-table-wrap">
                            <table className="mc-table">
                              <thead><tr><th>{ops.audit.columns.query}</th><th>{ops.audit.columns.position}</th><th>{ops.audit.columns.impressions}</th><th>{ops.audit.columns.clicks}</th></tr></thead>
                              <tbody>
                                {auditReport.metrics.queryMetrics.slice(0, 10).map((row) => (
                                  <tr key={row.query}>
                                    <td>{row.query}</td>
                                    <td>{row.position === null ? "—" : row.position.toFixed(1)}</td>
                                    <td>{row.impressions === null ? "—" : row.impressions.toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}</td>
                                    <td>{row.clicks === null ? "—" : row.clicks.toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          )}

          {tab === "setup" && (
            <div className="mc2-sitecols">
              <section className="mc2-card mc2-col-main" aria-labelledby="mc2-setup-tab">
                <div className="mc2-cardhead"><h2 id="mc2-setup-tab">{t.setupTitle}</h2><span className={CHIP[facts.every((fact) => fact.ok) ? "up" : "warn"]}>{facts.every((fact) => fact.ok) ? t.setupComplete : t.setupPartial}</span></div>
                <div className="mc2-sec">
                  {facts.map((fact) => (
                    <div className="mc2-chk" key={fact.key}>
                      <span className={fact.ok ? "mc2-ok" : "mc2-sbox no"} style={fact.ok ? undefined : { width: 20, height: 20 }}>{fact.ok ? "✓" : ""}</span>
                      {copy.fleet.setup[fact.key]}
                      <span className="mc2-muted" style={{ marginLeft: "auto", fontSize: 12 }}>{fact.ok ? copy.fleet.setupOk : copy.fleet.setupMissing}</span>
                    </div>
                  ))}
                </div>
                <div className="mc2-sec">
                  <dl className="mc-facts">
                    <div><dt>{ops.drawer.production}</dt><dd>{site.productionDomain ?? ops.drawer.notConnected}</dd></div>
                    <div><dt>{ops.drawer.owner}</dt><dd>{site.ownerId ?? ops.drawer.systemOwned}</dd></div>
                    <div><dt>{ops.drawer.live}</dt><dd>{site.lastLiveChangeAt ? ops.drawer.lastChange(relativeTime(site.lastLiveChangeAt, locale, now)) : ops.drawer.noChange}</dd></div>
                  </dl>
                </div>
                <div className="mc2-sec">
                  <div className="mc2-eyebrow" style={{ marginBottom: 8 }}>{ops.drawer.effects}</div>
                  {detail.effects.length === 0 ? <p className="mc2-muted">{ops.drawer.noEffects}</p> : detail.effects.map((effect) => (
                    <div className="mc2-li" key={effect.id}>
                      <span>{ops.drawer.effectName[effect.effect]}</span>
                      <span className={CHIP[effect.status === "failed" ? "down" : effect.status === "done" ? "up" : "warn"]}>{ops.drawer.effectStatus[effect.status]}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {tab === "client" && (
            <div className="mc2-sitecols">
              <section className="mc2-card mc2-col-main" aria-labelledby="mc2-client-tab">
                <div className="mc2-cardhead"><h2 id="mc2-client-tab">{ops.drawer.enquiries}</h2></div>
                {detail.enquiries.collectingSince === null ? <p className="mc2-empty">{ops.drawer.noEnquiries}</p> : (
                  <div className="mc2-sec">
                    <dl className="mc-facts">
                      <div><dt>{ops.drawer.enquiryLast}</dt><dd>{detail.enquiries.last28Days}</dd></div>
                      <div><dt>{ops.drawer.enquiryPrevious}</dt><dd>{detail.enquiries.previous28Days ?? "—"}</dd></div>
                    </dl>
                    <p className="mc2-muted" style={{ fontSize: 13 }}>{ops.drawer.enquirySince(formatDay(detail.enquiries.collectingSince, locale))}</p>
                    {(detail.enquiries.notifyFailures > 0 || detail.enquiries.notifySkipped > 0) && (
                      <p style={{ color: "var(--mc-error)" }}>{ops.drawer.enquiryEmailProblems(detail.enquiries.notifyFailures, detail.enquiries.notifySkipped)}</p>
                    )}
                  </div>
                )}
              </section>
              <section className="mc2-card mc2-col-side" aria-labelledby="mc2-client-mail">
                <div className="mc2-cardhead"><h2 id="mc2-client-mail">{t.clientTitle}</h2></div>
                <div style={{ padding: "12px 20px 16px", fontSize: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.weeklyEmail}</span><span className="mc2-muted">{t.weeklyEmailValue}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.approved}</span><span className="mc2-muted">{latestDeployed ? latestDeployed.summary : "—"}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span>{t.scores.enquiries}</span><span className="mc2-mono">{detail.enquiries.last28Days}</span></div>
                </div>
              </section>
            </div>
          )}

          {tab === "reports" && (
            <div className="mc2-sitecols">
              <section className="mc2-card mc2-col-main" aria-labelledby="mc2-reports-tab">
                <div className="mc2-cardhead"><h2 id="mc2-reports-tab">{ops.drawer.reports}</h2><span className="mc2-muted" style={{ fontSize: 13 }}>{detail.reports.length}</span></div>
                {detail.reports.length === 0 ? <p className="mc2-empty">{ops.drawer.noReports}</p> : detail.reports.map((report) => (
                  <div className="mc2-li" key={report.id}>
                    <strong>{formatMonth(report.month, locale)}</strong>
                    <span className={CHIP[report.status === "draft" ? "warn" : report.status === "approved" ? "up" : "neutral"]}>{ops.reports.status[report.status]}</span>
                    <span className="mc2-muted" style={{ marginLeft: "auto", marginRight: 8, fontSize: 12 }}>{report.quiet ? ops.reports.quiet : `${report.totals.shipped} · ${report.totals.measured}`}</span>
                    <button type="button" className="mc2-btn ghost xs" onClick={() => onOpenReport(report.id)}>{ops.action.open}</button>
                  </div>
                ))}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function capitalize(value: string): string { return value.charAt(0).toUpperCase() + value.slice(1); }

function AiStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: "16px 18px", borderRight: "1px solid var(--line-soft)" }}>
      <div className="mc2-eyebrow">{label}</div>
      <div className="mc2-serif" style={{ fontSize: 32, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ScoreCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="mc2-card mc2-scorecard">
      <div className="mc2-eyebrow">{label}</div>
      <div className="sval">{value}</div>
      <div className="mc2-muted" style={{ fontSize: 13 }}>{note}</div>
    </div>
  );
}

function AuditCard({
  latest,
  copy,
  ops,
  locale,
  now,
  busy,
  auditRunning,
  onOpen,
  onRun,
}: {
  latest: OperatorAuditPoint | null;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busy: boolean;
  auditRunning: boolean;
  onOpen(auditId: SeoAuditRunId): void;
  onRun(): void;
}) {
  const readable = latest?.status === "completed" || latest?.status === "partial";
  const statusLabel = latest ? (ops.activity.status[latest.status] ?? latest.status) : "";
  return (
    <section className="mc2-card" aria-labelledby="mc2-latest-audit">
      <div className="mc2-cardhead">
        <h2 id="mc2-latest-audit">{copy.site.latestAudit}</h2>
        {latest && <span className={CHIP[latest.status === "failed" ? "down" : readable ? "up" : "warn"]}>{statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}</span>}
      </div>
      <div className="mc2-sec">
        {!latest ? (
          <>
            <div className="mc2-eyebrow">{ops.audit.notAssessed}</div>
            <p className="mc2-muted" style={{ margin: "6px 0 0" }}>{ops.audit.notAssessedBody}</p>
          </>
        ) : auditRunning ? (
          <>
            <div className="mc2-eyebrow">{ops.audit.running}</div>
            <p className="mc2-muted" style={{ margin: "6px 0 0" }}>{ops.audit.runningBody}</p>
          </>
        ) : readable ? (
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span className="mc2-serif" style={{ fontSize: 56, lineHeight: 1 }}>{latest.score !== null ? Math.round(latest.score) : "—"}</span>
            <span className="mc2-muted" style={{ fontSize: 13 }}>{ops.audit.outOf}{latest.grade ? ` · ${ops.audit.grade(latest.grade)}` : ""}<br />{ops.audit.captured(relativeTime(latest.capturedAt, locale, now))}</span>
          </div>
        ) : (
          <p className="mc2-muted" style={{ margin: 0 }}>{relativeTime(latest.capturedAt, locale, now)}</p>
        )}
        <div className="mc2-actions" style={{ marginTop: 14 }}>
          {readable && latest && <button type="button" className="mc2-btn sm" onClick={() => onOpen(latest.id)}>{ops.audit.openReport}</button>}
          <button type="button" className="mc2-btn ghost sm" disabled={busy || auditRunning} onClick={onRun}>{busy || auditRunning ? ops.action.working : ops.audit.runAgain}</button>
        </div>
      </div>
    </section>
  );
}

function ChangeRow({
  row,
  copy,
  ops,
  locale,
  now,
  busyKey,
  canRollback,
  onDecide,
  onRecommendation,
}: {
  row: OperatorChangeSetRow;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  canRollback: boolean;
  onDecide(changeSetId: string, action: DecisionAction, busyKey: string): void;
  onRecommendation(recommendationId: string, action: RecommendationAction, busyKey: string): void;
}) {
  const [confirming, setConfirming] = useState(false);
  const key = (action: string) => `change:${row.id}:${action}`;
  const busy = busyKey.startsWith(`change:${row.id}:`);
  return (
    <div className="mc2-sec">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span className={CHIP[row.status === "failed" ? "down" : row.status === "deployed" ? "up" : row.status === "awaiting_approval" ? "warn" : "neutral"]}>
          {ops.pipeline.changeStatus[row.status] ?? row.status}
        </span>
        {row.safetyClass && <span className={CHIP.neutral}>{ops.pipeline.safety[row.safetyClass]}</span>}
        <span className="mc2-muted mc2-mono" style={{ marginLeft: "auto", fontSize: 12 }}>{relativeTime(row.deployedAt ?? row.createdAt, locale, now)}</span>
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, marginTop: 6 }}>{row.summary}</div>
      {row.errorMessage && <div style={{ color: "var(--mc-error)", fontSize: 13, marginTop: 4 }}>{row.errorMessage}</div>}
      {confirming ? (
        <div className="mc2-stopped" style={{ marginTop: 10 }}>
          <strong>{ops.confirm.rollback}</strong>
          <div className="mc2-actions" style={{ marginTop: 6 }}>
            <button type="button" className="mc2-btn sm" disabled={busy} onClick={() => { setConfirming(false); onDecide(row.id, "rollback", key("rollback")); }}>{ops.action.rollback}</button>
            <button type="button" className="mc2-btn ghost sm" onClick={() => setConfirming(false)}>{ops.action.cancel}</button>
          </div>
        </div>
      ) : (
        <div className="mc2-actions" style={{ marginTop: 10 }}>
          {row.status === "awaiting_approval" && (
            <>
              <button type="button" className="mc2-btn xs" disabled={busy} onClick={() => onDecide(row.id, "approve", key("approve"))}>{busyKey === key("approve") ? ops.action.working : ops.action.approve}</button>
              <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => onDecide(row.id, "reject", key("reject"))}>{ops.action.reject}</button>
            </>
          )}
          {row.status === "failed" && row.recommendationId && (
            <button type="button" className="mc2-btn xs" disabled={busy} onClick={() => onRecommendation(row.recommendationId as string, "preview", key("retry"))}>{busyKey === key("retry") ? ops.action.working : ops.action.retry}</button>
          )}
          {row.previewUrl && <a className="mc2-linkbtn" style={{ fontSize: 13 }} href={row.previewUrl} target="_blank" rel="noreferrer">{ops.action.openPreview} ↗</a>}
          {row.status === "deployed" && canRollback && <button type="button" className="mc2-btn ghost xs" disabled={busy} onClick={() => setConfirming(true)}>{ops.action.rollbackQuiet}</button>}
        </div>
      )}
    </div>
  );
}

function buildDays(
  detail: OperatorSiteDetailV1 | null,
  now: number,
  locale: "en" | "fr",
  copy: MC2Copy,
  ops: OperatorCopy,
  handlers: { onOpenChange: () => void; onOpenAudit: (id: SeoAuditRunId) => void; onOpenReport: (id: MonthlyReportId) => void },
): Array<{ iso: string; label: string; events: Array<{ kind: string; text: string; onClick?: () => void }> }> {
  const days: Array<{ iso: string; label: string; events: Array<{ kind: string; text: string; onClick?: () => void }> }> = [];
  for (let offset = 6; offset >= 0; offset -= 1) {
    const date = new Date(now - offset * 86_400_000);
    const iso = date.toISOString().slice(0, 10);
    const events: Array<{ kind: string; text: string; onClick?: () => void }> = [];
    for (const row of detail?.changeSets ?? []) {
      const stamp = row.deployedAt ?? row.rolledBackAt ?? row.createdAt;
      if (stamp.slice(0, 10) !== iso) continue;
      if (row.deployedAt) events.push({ kind: "ship", text: `${copy.site.eventShipped}: ${row.summary}`, onClick: handlers.onOpenChange });
      else if (row.rolledBackAt) events.push({ kind: "alert", text: `${copy.site.eventRolledBack}: ${row.summary}`, onClick: handlers.onOpenChange });
      else events.push({ kind: "preview", text: `${copy.site.eventPreview}: ${ops.pipeline.changeStatus[row.status] ?? row.status} — ${row.summary}`, onClick: handlers.onOpenChange });
    }
    for (const audit of detail?.audits ?? []) {
      if (audit.capturedAt.slice(0, 10) !== iso) continue;
      events.push({ kind: "audit", text: `${copy.site.eventAudit}: ${audit.score !== null ? `${Math.round(audit.score)}/100` : ops.activity.status[audit.status] ?? audit.status}`, onClick: () => handlers.onOpenAudit(audit.id) });
    }
    for (const report of detail?.reports ?? []) {
      if (report.createdAt.slice(0, 10) !== iso) continue;
      events.push({ kind: "client", text: `${copy.site.eventReport}: ${formatMonth(report.month, locale)}`, onClick: () => handlers.onOpenReport(report.id) });
    }
    days.push({
      iso,
      label: new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "short", day: "numeric" }).format(date),
      events,
    });
  }
  return days;
}

function buildPipe(detail: OperatorSiteDetailV1 | null, ops: OperatorCopy, locale: "en" | "fr"): Array<{ label: string; cards: Array<{ key: string; title: string; meta: string }> }> {
  const recs = detail?.recommendations ?? [];
  const changes = detail?.changeSets ?? [];
  const found = recs.filter((row) => ["new", "viewed", "saved"].includes(row.status)).slice(0, 2).map((row) => ({ key: row.id, title: row.title, meta: ops.drawer.type[row.type] ?? row.type }));
  const preview = changes.filter((row) => ["requested", "planning", "editing", "validating", "preview_ready"].includes(row.status)).slice(0, 2).map((row) => ({ key: row.id, title: row.summary, meta: ops.pipeline.changeStatus[row.status] ?? row.status }));
  const approve = changes.filter((row) => row.status === "awaiting_approval").slice(0, 2).map((row) => ({ key: row.id, title: row.summary, meta: ops.today.kind.approve }));
  const live = changes.filter((row) => row.status === "deployed").slice(0, 2).map((row) => ({ key: row.id, title: row.summary, meta: row.deployedAt ? `${ops.pipeline.stage.deployed} ${new Date(row.deployedAt).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short" })}` : ops.pipeline.stage.deployed }));
  const measured = recs.filter((row) => row.status === "measured").slice(0, 2).map((row) => ({ key: row.id, title: row.title, meta: ops.pipeline.stage.measured }));
  return [
    { label: ops.pipeline.stage.detected, cards: found },
    { label: ops.pipeline.changeStatus.preview_ready ?? "Preview", cards: preview },
    { label: ops.pipeline.stage.to_approve, cards: approve },
    { label: ops.pipeline.stage.deployed, cards: live },
    { label: ops.pipeline.stage.measured, cards: measured },
  ];
}
